import { forwardRef } from "react";
import {
    Archive, Check, ChevronDown, Clock, Ellipsis, Flag, FolderInput, ListPlus, MailOpen, Mail as MailIcon,
    PanelBottom, PanelLeft, PanelRight, PanelsTopLeft, Pin, ShieldAlert, Tag, Trash2, Undo2, Rows3, MessagesSquare
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { DENSITIES, updatePreferences, usePreferences, type Density, type ReadingPanePref } from "@/lib/preferences";
import type { Folder } from "@/api/mailApi";
import { SNOOZE_PRESETS } from "./SnoozeMenu";

export interface CommandBarState {
    /** Something to act on: checked rows, or the open message. */
    hasTarget: boolean;
    /** How many rows are checked (0 → the open message). */
    checkedCount: number;
    /** Drafts only support Delete. */
    isDrafts: boolean;
    anyUnread: boolean;
    allFlagged: boolean;
    allPinned: boolean;
    canUndo: boolean;
}

export interface CommandBarActions {
    onDelete: () => void;
    onArchive: () => void;
    onReportJunk: () => void;
    onMove: (folderId: string) => void;
    onApplyLabel: (labelId: string) => void;
    onToggleRead: () => void;
    onToggleFlag: () => void;
    onTogglePin: () => void;
    onSnooze: (wakeAt: Date) => void;
    onCreateTask: (listKey: string) => void;
    onUndo: () => void;
    onPrint?: () => void;
    onDownloadEml?: () => void;
}

interface MailCommandBarProps extends CommandBarState, CommandBarActions {
    folders: Folder[];
    currentFolderId: string;
    labels: { id: string; name: string; color: string }[];
    taskTargets: { key: string; label: string; emoji?: string | null }[];
    /** Hides Direct / Updates from the Layout menu outside the inbox. */
    tabsAvailable: boolean;
}

const DENSITY_LABEL: Record<Density, string> = { roomy: "Roomy", cozy: "Cozy", compact: "Compact" };
const PANE_OPTIONS: ReadonlyArray<{ value: ReadingPanePref; label: string; icon: typeof PanelRight }> = [
    { value: "right", label: "Right", icon: PanelRight },
    { value: "bottom", label: "Bottom", icon: PanelBottom },
    { value: "off", label: "Hidden", icon: Rows3 }
];

/**
 * One-line, simplified-ribbon command bar over the list and the reader
 * (Doc/MAIL_UI_REDESIGN.md, approved mock-up): Delete · Archive · Report |
 * Move to · Categorise | Read/Unread · Flag · Pin · Snooze | Create task ·
 * Undo · More, and on the right Density and Layout. Acts on the checked rows,
 * or on the open message when nothing is checked. Scrolls sideways on narrow
 * screens rather than wrapping.
 */
export function MailCommandBar(props: MailCommandBarProps) {
    const { hasTarget, isDrafts, checkedCount } = props;
    const prefs = usePreferences();
    const density = prefs.appearance.density;
    const off = !hasTarget;
    const triage = off || isDrafts;

    const moveTargets = props.folders.filter((f) => f.id !== props.currentFolderId && f.kind !== "flagged" && f.kind !== "drafts");

    return (
        <div
            role="toolbar"
            aria-label={checkedCount > 0 ? `Actions for ${checkedCount} selected` : "Mail commands"}
            className="flex h-11 shrink-0 items-center gap-0.5 overflow-x-auto border-b border-[var(--mail-line)] bg-card px-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
            {checkedCount > 0 ? (
                <span className="mr-1 whitespace-nowrap rounded bg-[var(--mail-primary-soft)] px-2 py-1 text-xs font-semibold text-primary tabular-nums">
                    {checkedCount} selected
                </span>
            ) : null}

            <Cmd icon={<Trash2 className="h-4 w-4 text-[var(--mail-danger)]" />} label="Delete" disabled={off} onClick={props.onDelete} />
            <Cmd icon={<Archive className="h-4 w-4" />} label="Archive" disabled={triage} onClick={props.onArchive} />
            <Cmd icon={<ShieldAlert className="h-4 w-4" />} label="Report" title="Report as junk" disabled={triage} onClick={props.onReportJunk} />

            <Sep />

            <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={triage}>
                    <CmdTrigger icon={<FolderInput className="h-4 w-4" />} label="Move to" disabled={triage} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Move to folder</DropdownMenuLabel>
                    {moveTargets.map((f) => (
                        <DropdownMenuItem key={f.id} onSelect={() => props.onMove(f.id)}>
                            <span className="truncate">{displayFolderName(f)}</span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={triage}>
                    <CmdTrigger icon={<Tag className="h-4 w-4" />} label="Categorise" disabled={triage} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-80 w-56 overflow-y-auto">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Apply label</DropdownMenuLabel>
                    {props.labels.length === 0 ? (
                        <p className="px-2 py-1.5 text-xs text-muted-foreground">No labels yet — create them in Settings → Labels.</p>
                    ) : props.labels.map((l) => (
                        <DropdownMenuItem key={l.id} onSelect={() => props.onApplyLabel(l.id)}>
                            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: l.color }} aria-hidden />
                            <span className="truncate">{l.name}</span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <Sep />

            <Cmd
                icon={props.anyUnread ? <MailOpen className="h-4 w-4" /> : <MailIcon className="h-4 w-4" />}
                label={props.anyUnread ? "Mark read" : "Mark unread"}
                disabled={triage}
                onClick={props.onToggleRead}
            />
            <Cmd
                icon={<Flag className={cn("h-4 w-4", props.allFlagged && "fill-[var(--mail-danger)] text-[var(--mail-danger)]")} />}
                title={props.allFlagged ? "Unflag" : "Flag"}
                pressed={props.allFlagged}
                disabled={triage}
                onClick={props.onToggleFlag}
            />
            <Cmd
                icon={<Pin className={cn("h-4 w-4", props.allPinned && "fill-primary text-primary")} />}
                title={props.allPinned ? "Unpin" : "Pin to top"}
                pressed={props.allPinned}
                disabled={triage}
                onClick={props.onTogglePin}
            />
            <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={triage}>
                    <CmdTrigger icon={<Clock className="h-4 w-4" />} title="Snooze" disabled={triage} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Snooze until</DropdownMenuLabel>
                    {SNOOZE_PRESETS.map((p) => (
                        <DropdownMenuItem key={p.label} onSelect={() => props.onSnooze(p.compute())}>
                            <span className="flex-1">{p.label}</span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <Sep />

            <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={triage}>
                    <CmdTrigger icon={<ListPlus className="h-4 w-4 text-primary" />} label="Create task" disabled={triage} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-72 w-52 overflow-y-auto">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Add to list</DropdownMenuLabel>
                    {props.taskTargets.map((t) => (
                        <DropdownMenuItem key={t.key} onSelect={() => props.onCreateTask(t.key)}>
                            {t.emoji ? <span aria-hidden>{t.emoji}</span> : null}
                            {t.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <Cmd icon={<Undo2 className="h-4 w-4" />} title="Undo (Z)" disabled={!props.canUndo} onClick={props.onUndo} />
            {props.onPrint || props.onDownloadEml ? (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild disabled={triage}>
                        <CmdTrigger icon={<Ellipsis className="h-4 w-4" />} title="More" caret={false} disabled={triage} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                        {props.onPrint ? <DropdownMenuItem onSelect={props.onPrint}>Print</DropdownMenuItem> : null}
                        {props.onDownloadEml ? <DropdownMenuItem onSelect={props.onDownloadEml}>Download as EML</DropdownMenuItem> : null}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : null}

            <span className="min-w-3 flex-1" />

            <div role="group" aria-label="Density" className="hidden shrink-0 overflow-hidden rounded border border-[var(--mail-line-strong)] md:inline-flex">
                {DENSITIES.map((d, i) => (
                    <button
                        key={d}
                        type="button"
                        aria-pressed={density === d}
                        onClick={() => updatePreferences({ appearance: { density: d } })}
                        className={cn(
                            "h-7 px-2.5 text-xs text-[var(--mail-text-2)] transition-colors hover:bg-[var(--mail-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                            i > 0 && "border-l border-[var(--mail-line-strong)]",
                            density === d && "bg-[var(--mail-primary-soft)] font-semibold text-primary hover:bg-[var(--mail-primary-soft)]"
                        )}
                    >
                        {DENSITY_LABEL[d]}
                    </button>
                ))}
            </div>
            <LayoutMenu tabsAvailable={props.tabsAvailable} />
        </div>
    );
}

/** Reading pane position, panes and list options — all saved preferences. */
function LayoutMenu({ tabsAvailable }: { tabsAvailable: boolean }) {
    const layout = usePreferences((p) => p.layout);
    const list = usePreferences((p) => p.list);
    const density = usePreferences((p) => p.appearance.density);
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <CmdTrigger icon={<PanelsTopLeft className="h-4 w-4" />} title="Layout" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Reading pane</DropdownMenuLabel>
                {PANE_OPTIONS.map((o) => (
                    <DropdownMenuItem key={o.value} onSelect={() => updatePreferences({ layout: { readingPane: o.value } })}>
                        <o.icon className="h-4 w-4" />
                        <span className="flex-1">{o.label}</span>
                        {layout.readingPane === o.value ? <Check className="h-4 w-4 text-primary" /> : null}
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">Panes and list</DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => updatePreferences({ layout: { folderPane: !layout.folderPane } })}>
                    <PanelLeft className="h-4 w-4" />
                    <span className="flex-1">Folder pane</span>
                    {layout.folderPane ? <Check className="h-4 w-4 text-primary" /> : null}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => updatePreferences({ layout: { conversationView: !layout.conversationView } })}>
                    <MessagesSquare className="h-4 w-4" />
                    <span className="flex-1">Group by conversation</span>
                    {layout.conversationView ? <Check className="h-4 w-4 text-primary" /> : null}
                </DropdownMenuItem>
                {tabsAvailable ? (
                    <DropdownMenuItem onSelect={() => updatePreferences({ list: { inboxTabs: !list.inboxTabs } })}>
                        <Rows3 className="h-4 w-4" />
                        <span className="flex-1">Direct / Updates tabs</span>
                        {list.inboxTabs ? <Check className="h-4 w-4 text-primary" /> : null}
                    </DropdownMenuItem>
                ) : null}
                {/* Density lives in the bar on wide screens; here for narrow ones. */}
                <div className="md:hidden">
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Density</DropdownMenuLabel>
                    {DENSITIES.map((d) => (
                        <DropdownMenuItem key={d} onSelect={() => updatePreferences({ appearance: { density: d } })}>
                            <span className="flex-1">{DENSITY_LABEL[d]}</span>
                            {density === d ? <Check className="h-4 w-4 text-primary" /> : null}
                        </DropdownMenuItem>
                    ))}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const CMD =
    "inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded px-2 text-[13px] text-foreground transition-colors " +
    "hover:bg-[var(--mail-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
    "disabled:pointer-events-none disabled:opacity-40 data-[state=open]:bg-[var(--mail-hover)]";

function Cmd({
    icon, label, title, pressed, disabled, onClick
}: {
    icon: React.ReactNode;
    label?: string;
    title?: string;
    pressed?: boolean;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            className={cn(CMD, !label && "px-[7px]")}
            title={title ?? label}
            aria-label={label ? undefined : title}
            aria-pressed={pressed}
            disabled={disabled}
            onClick={onClick}
        >
            {icon}
            {label ? <span className="hidden lg:inline">{label}</span> : null}
        </button>
    );
}

/** Dropdown trigger styled like a command; Radix passes its props and ref through asChild. */
const CmdTrigger = forwardRef<
    HTMLButtonElement,
    { icon: React.ReactNode; label?: string; caret?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ icon, label, title, caret = true, className, ...rest }, ref) => (
    <button
        ref={ref}
        type="button"
        {...rest}
        className={cn(CMD, !label && "px-[7px]", className)}
        title={title ?? label}
        aria-label={label ? undefined : title}
    >
        {icon}
        {label ? <span className="hidden lg:inline">{label}</span> : null}
        {caret ? <ChevronDown className="h-3 w-3 text-[var(--mail-text-3)]" aria-hidden /> : null}
    </button>
));
CmdTrigger.displayName = "CmdTrigger";

function Sep() {
    return <span aria-hidden className="mx-1.5 h-5 w-px shrink-0 bg-[var(--mail-line)]" />;
}

/** "INBOX" → "Inbox": IMAP servers name the inbox in capitals; show it like any other folder. */
export function displayFolderName(f: Pick<Folder, "name" | "kind">): string {
    if (f.kind === "inbox") return "Inbox";
    const n = f.name.trim();
    return n.length > 1 && n === n.toUpperCase() && /[A-Z]/.test(n) ? n.charAt(0) + n.slice(1).toLowerCase() : n;
}
